import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  fetchPostsLibrary,
  fetchDealerHomePosts,
  saveDealerHomePosts,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  GripVertical,
  X,
  Plus,
  Search,
  Loader2,
  Home as HomeIcon,
  Check,
  Library,
  Video,
  FileCode,
} from "lucide-react";

/**
 * Home page tabs picker.
 *
 * Edits the dealer's `home_post_slugs` field (comma-separated order of
 * Posts.slug values). The first tab on /d/:slug is always "Home" (the dealer's
 * existing HTML widget), followed by these picked posts.
 *
 *  • Drag the grip icon to reorder selected tabs.
 *  • Click ✕ to drop a tab.
 *  • "Add from Library" opens a modal listing the master Posts table,
 *    with the "Home" admin-default ones highlighted.
 *  • Saving an empty list reverts to the admin defaults (Home=true posts).
 */
const HOME_CHIP = { slug: "__home__", title: "Home", is_home: true };

export default function HomePostsPicker({ userRecordId, slug }) {
  const [library, setLibrary] = useState([]);
  const [selected, setSelected] = useState([]); // [{slug, title, video_url, html, home}]
  const [usingDefaults, setUsingDefaults] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [libOpen, setLibOpen] = useState(false);
  const [libSearch, setLibSearch] = useState("");
  const initialKey = useRef("");
  const lastSavedKey = useRef("");

  const load = useCallback(async () => {
    if (!userRecordId) return;
    setLoading(true);
    try {
      const [lib, own] = await Promise.all([
        fetchPostsLibrary({ userRecordId }),
        fetchDealerHomePosts(userRecordId),
      ]);
      const libItems = lib?.items || [];
      setLibrary(libItems);
      const raw = own?.items || [];
      // Home HTML tab is a draggable chip too; it sits first unless the dealer moved it.
      const items = raw.some((p) => p.is_home) ? raw : [HOME_CHIP, ...raw];
      setSelected(items);
      setUsingDefaults(!!own?.using_defaults);
      initialKey.current = items.map((p) => p.slug).join(",");
      lastSavedKey.current = initialKey.current;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load posts");
    } finally {
      setLoading(false);
    }
  }, [userRecordId]);

  useEffect(() => {
    load();
  }, [load]);

  const currentKey = selected.map((p) => p.slug).join(",");
  const dirty = currentKey !== lastSavedKey.current;

  const selectedSlugs = useMemo(
    () => new Set(selected.map((p) => p.slug)),
    [selected]
  );

  // ----- Selected tabs reorder -------------------------------------------
  const dragIndex = useRef(null);

  function onDragStart(idx, e) {
    dragIndex.current = idx;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(idx));
  }
  function onDragOver(e) {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }
  function onDrop(targetIdx, e) {
    e.preventDefault();
    const from = dragIndex.current ?? Number(e.dataTransfer.getData("text/plain"));
    if (Number.isNaN(from) || from === targetIdx) return;
    setSelected((arr) => {
      const next = arr.slice();
      const [item] = next.splice(from, 1);
      next.splice(targetIdx, 0, item);
      return next;
    });
    dragIndex.current = null;
  }

  function removeAt(idx) {
    setSelected((arr) => (arr[idx]?.is_home ? arr : arr.slice(0, idx).concat(arr.slice(idx + 1))));
  }

  function addPost(post) {
    if (selectedSlugs.has(post.slug)) return;
    setSelected((arr) => arr.concat(post));
  }

  function resetToDefaults() {
    const defs = library.filter((p) => p.home && p.is_admin_post);
    setSelected([HOME_CHIP, ...defs]);
  }
  const postCount = selected.filter((p) => !p.is_home).length;

  async function persist() {
    setSaving(true);
    try {
      await saveDealerHomePosts(
        userRecordId,
        postCount ? selected.map((p) => p.slug) : []
      );
      lastSavedKey.current = currentKey;
      setUsingDefaults(postCount === 0);
      toast.success(
        postCount
          ? "Home tabs saved"
          : "Cleared — reverted to defaults"
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  }

  // ----- Library filtering -----------------------------------------------
  const libFiltered = useMemo(() => {
    const q = libSearch.trim().toLowerCase();
    return library
      .filter((p) => !q || (p.title + " " + p.slug).toLowerCase().includes(q))
      .sort((a, b) => {
        // Home defaults first, then alpha
        if (a.home !== b.home) return a.home ? -1 : 1;
        return (a.title || "").localeCompare(b.title || "");
      });
  }, [library, libSearch]);

  if (loading) {
    return (
      <div className="py-6 flex items-center justify-center text-zinc-500 text-sm">
        <Loader2 className="animate-spin mr-2" size={16} /> Loading posts…
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="home-posts-picker">
      <p className="text-xs text-zinc-500">
        Drag the grip handle to reorder tabs. The first tab on your{" "}
        <span className="font-mono text-zinc-700">
          orevital.com/d/{slug || "you"}
        </span>{" "}
        home page shows your <strong>Home</strong> HTML widget plus these posts —
        drag the Home chip too if you want a post first.{" "}
        {usingDefaults && selected.length > 0 && (
          <span className="text-emerald-700 font-medium">
            Showing the admin defaults — pick &amp; reorder to override.
          </span>
        )}
      </p>

      {/* ===== Selected tabs row ===== */}
      <Card className="rounded-2xl border-zinc-200 p-3 bg-zinc-50">
        <div className="flex flex-wrap gap-2 items-center">
          {selected.map((p, idx) => (
            <div
              key={p.slug}
              draggable
              onDragStart={(e) => onDragStart(idx, e)}
              onDragOver={onDragOver}
              onDrop={(e) => onDrop(idx, e)}
              className={`group inline-flex items-center gap-1.5 px-2.5 py-2 rounded-lg border cursor-move text-xs font-semibold ${
                p.is_home
                  ? "bg-violet-100 border-violet-200 text-violet-700"
                  : "bg-white border-zinc-200 hover:border-violet-300 hover:shadow-sm text-zinc-800"
              }`}
              data-testid={p.is_home ? "home-tab-home" : `home-tab-${p.slug}`}
              title={p.is_home ? "Your Home HTML widget — drag to move" : p.slug}
            >
              <GripVertical size={12} className={p.is_home ? "text-violet-400" : "text-zinc-400"} />
              {p.is_home && <HomeIcon size={12} />}
              <span className="max-w-[140px] truncate">{p.title || p.slug}</span>
              {!p.is_home && (
                <button
                  type="button"
                  onClick={() => removeAt(idx)}
                  className="ml-0.5 text-zinc-400 hover:text-rose-600"
                  data-testid={`home-tab-remove-${p.slug}`}
                  aria-label={`Remove ${p.title}`}
                >
                  <X size={12} />
                </button>
              )}
            </div>
          ))}

          <Button
            type="button"
            onClick={() => setLibOpen(true)}
            className="h-9 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold"
            data-testid="home-tabs-add-btn"
          >
            <Library size={13} className="mr-1.5" /> Add from Library
          </Button>
        </div>

        <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={resetToDefaults}
              className="text-[11px] font-semibold text-violet-700 hover:underline"
              data-testid="home-tabs-reset"
            >
              Reset to admin defaults
            </button>
            <span className="text-[11px] text-zinc-400">
              · {postCount} post{postCount === 1 ? "" : "s"}
            </span>
          </div>
          <Button
            type="button"
            onClick={persist}
            disabled={!dirty || saving}
            className={`h-9 rounded-full px-4 text-xs font-semibold ${
              dirty
                ? "bg-violet-600 hover:bg-violet-700 text-white"
                : "bg-zinc-200 text-zinc-500"
            }`}
            data-testid="home-tabs-save"
          >
            {saving ? (
              <>
                <Loader2 size={13} className="animate-spin mr-1.5" /> Saving…
              </>
            ) : dirty ? (
              <>
                <Check size={13} className="mr-1.5" /> Save tab order
              </>
            ) : (
              "Saved"
            )}
          </Button>
        </div>
      </Card>

      {/* ===== Library modal ===== */}
      {libOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start sm:items-center justify-center px-3 py-6"
          onClick={() => setLibOpen(false)}
          data-testid="home-tabs-library-modal"
        >
          <div
            className="w-full max-w-lg max-h-[85vh] flex flex-col rounded-2xl bg-white shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-zinc-200 flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-zinc-900">
                  Post Library
                </div>
                <div className="text-[11px] text-zinc-500">
                  {libFiltered.length} of {library.length} posts · admin-default
                  posts shown first
                </div>
              </div>
              <button
                type="button"
                onClick={() => setLibOpen(false)}
                className="w-8 h-8 rounded-full bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center"
                data-testid="home-tabs-library-close"
              >
                <X size={14} />
              </button>
            </div>

            <div className="p-3 border-b border-zinc-200">
              <div className="relative">
                <Search
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"
                />
                <Input
                  value={libSearch}
                  onChange={(e) => setLibSearch(e.target.value)}
                  placeholder="Search posts…"
                  className="h-10 rounded-lg pl-9"
                  data-testid="home-tabs-library-search"
                />
              </div>
            </div>

            <div className="overflow-y-auto px-2 py-2 space-y-1.5">
              {libFiltered.length === 0 ? (
                <div className="py-10 text-center text-sm text-zinc-500">
                  No posts match.
                </div>
              ) : (
                libFiltered.map((p) => {
                  const picked = selectedSlugs.has(p.slug);
                  return (
                    <button
                      key={p.id || p.slug}
                      type="button"
                      onClick={() => {
                        if (picked) {
                          setSelected((arr) =>
                            arr.filter((x) => x.slug !== p.slug)
                          );
                        } else {
                          addPost(p);
                        }
                      }}
                      className={`w-full text-left flex items-center gap-2 px-2.5 py-2 rounded-lg border transition-colors ${
                        picked
                          ? "bg-violet-50 border-violet-300"
                          : "bg-white border-zinc-200 hover:border-violet-200 hover:bg-violet-50/40"
                      }`}
                      data-testid={`home-tabs-library-item-${p.slug}`}
                    >
                      <span
                        className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 ${
                          picked
                            ? "bg-violet-600 border-violet-600 text-white"
                            : "border-zinc-300"
                        }`}
                      >
                        {picked ? <Check size={12} /> : <Plus size={12} />}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-semibold text-zinc-900 truncate flex items-center gap-1.5">
                          {p.title || p.slug}
                          {p.home && (
                            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-100 text-emerald-700">
                              HOME
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-zinc-500 font-mono truncate">
                          /{p.slug}
                        </div>
                      </div>
                      <span className="flex items-center gap-1 text-[10px] text-zinc-400 shrink-0">
                        {p.video_url && <Video size={11} />}
                        {p.html && <FileCode size={11} />}
                      </span>
                    </button>
                  );
                })
              )}
            </div>

            <div className="p-3 border-t border-zinc-200 flex justify-end">
              <Button
                onClick={() => setLibOpen(false)}
                className="h-9 rounded-full bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold"
                data-testid="home-tabs-library-done"
              >
                Done ({selected.length} selected)
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
